import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiShoppingCart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useCountCombinedEventsByStatusQuery } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import {
  BUCKET_META,
  bucketQuery,
  countBadgesByStatus,
  type BucketKey,
} from "../../../../common/layout/v2/statusBuckets";
import { useNavLabel } from "../../../../common/layout/v2/useNavLabel";
import { useSub } from "../../../../common/subscription/useSub";
import { urls } from "../../../../urls";
import { DoughnutBreakdown } from "./DoughnutBreakdown";
import { PanelBody, PanelCard, PanelHeader } from "./HomePanel";

/** The lifecycle, left to right. Drafts at one end, finished at the other. */
const PHASES: BucketKey[] = ["arbeid", "kunngjort", "evaluering", "tildeling", "fullfort"];

/** Identity tokens for the five phases, in lifecycle order — see their definition in config.ts. */
const PHASE_COLOR: Record<BucketKey, string> = {
  arbeid: "smChartPhaseDraft",
  kunngjort: "smChartPhaseAnnounced",
  evaluering: "smChartPhaseEvaluation",
  tildeling: "smChartPhaseAward",
  fullfort: "smChartPhaseCompleted",
};

/**
 * The organization's procurements by phase, as the counterpart to the agreements-expiry chart. The
 * counts come from the query the sidebar badges already run, so the slices agree with the menu and
 * the phase strip in the Procurements card, and cost no extra request.
 */
export const ProcurementPhasesPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navLabel = useNavLabel();
  const sub = useSub();
  const authState = useLoggedInWithOrgContextState();

  const { data, isLoading, isError } = useCountCombinedEventsByStatusQuery(
    sub.hasBuyerAccess && sub.canViewSourcingEvents ? { orgId: authState.selectedOrg.id } : skipToken
  );

  const badges = useMemo(() => countBadgesByStatus(data?.counts ?? []), [data]);
  const counts = PHASES.map((phase) => badges[BUCKET_META[phase].badgeKey] ?? 0);
  const total = counts.reduce((sum, count) => sum + count, 0);

  const openPhase = (index: number) =>
    navigate(`${urls.v2.sourcingEvents.viewAll}?${bucketQuery(PHASES[index])}`);

  return (
    <PanelCard>
      <PanelHeader
        titleKey="Procurements by phase"
        icon={FiShoppingCart}
        count={total}
        seeAllTo={urls.v2.sourcingEvents.viewAll}
      />
      <PanelBody isLoading={isLoading} isError={isError} isEmpty={total === 0} emptyKey="No procurements yet">
        <DoughnutBreakdown
          slices={PHASES.map((phase, index) => ({
            key: phase,
            label: navLabel(BUCKET_META[phase].labelKey),
            colorToken: PHASE_COLOR[phase],
            count: counts[index],
          }))}
          totalLabel={t("Procurements")}
          onSelect={openPhase}
        />
      </PanelBody>
    </PanelCard>
  );
};
